import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useCart } from "../context/AppContext";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Minus, Plus, ArrowLeft, FileText, FlaskConical, ShieldCheck, Truck, Package } from "lucide-react";
import { TrustSeal, DeliveryPromise } from "../components/TrustSeal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then(r => {
      setProduct(r.data);
      setVariantId(r.data.variants[0].id);
    });
  }, [slug]);

  if (!product) return <div className="min-h-screen bg-black pt-32 text-white/40 text-center font-mono tracking-[0.25em] uppercase">Loading</div>;
  const variant = product.variants.find(v => v.id === variantId) || product.variants[0];
  const tested = product.testing_status === "verified";

  const handleAdd = () => {
    add(product, variant, qty);
    toast.success(`${product.name} ${variant.strength} added`);
  };

  return (
    <div className="bg-black min-h-screen pt-24">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8">
        <Link to="/shop" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase text-white/50 hover:text-white transition-colors">
          <ArrowLeft size={13} /> Back to Shop
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          {/* Image */}
          <div>
            <div className="relative aspect-square border border-white/5 overflow-hidden bg-[#050508]">
              {product.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "brightness(1.05) contrast(1.08) saturate(1.1)" }}
                />
              ) : (
                <div className="absolute inset-0 bl-vial-placeholder flex items-center justify-center">
                  <div className="font-display text-white text-2xl">{product.name}</div>
                </div>
              )}
            </div>
            {/* Pagination dots */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="w-2 h-2 rounded-full bg-white" />
              <span className="w-2 h-2 rounded-full bg-white/25" />
              <span className="w-2 h-2 rounded-full bg-white/25" />
            </div>
          </div>

          {/* Buy panel */}
          <div>
            <h1 className="bl-heading text-white text-4xl sm:text-5xl">{product.name}</h1>

            {/* Status row: TESTED · IN STOCK */}
            <div className="mt-4 flex items-center gap-4">
              {tested && (
                <span data-testid="pdp-tested-badge" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[color:var(--bl-green)]">
                  <span className="w-2 h-2 rounded-full bg-[color:var(--bl-green)]" style={{ boxShadow: "0 0 8px var(--bl-green-soft)" }} />
                  Tested
                </span>
              )}
              {tested && <span className="text-white/20">|</span>}
              <span className="inline-flex items-center font-mono text-[11px] tracking-[0.25em] uppercase text-[color:var(--bl-blue)]">
                {variant.in_stock ? "In Stock" : "Sold Out"}
              </span>
            </div>

            <p className="text-white/60 mt-6 max-w-lg leading-relaxed">{product.tagline}</p>

            {/* Strength pills */}
            <div className="mt-10">
              <div className="bl-eyebrow mb-4">{product.option_label || "Select Strength"}</div>
              <div data-testid="pdp-strength-select" className="flex flex-wrap gap-2">
                {product.variants.map(v => {
                  const active = v.id === variantId;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setVariantId(v.id)}
                      data-testid={`pdp-strength-${v.id}`}
                      className={`px-5 py-3 font-mono text-[13px] tracking-[0.18em] uppercase transition-colors ${
                        active
                          ? "border border-[color:var(--bl-blue)] text-[color:var(--bl-blue)]"
                          : "border border-white/12 text-white/85 hover:border-white/40"
                      }`}
                      style={active ? { boxShadow: "0 0 0 1px var(--bl-blue-soft), 0 0 18px rgba(0,229,255,0.15)" } : undefined}
                    >
                      {v.strength}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Price */}
            <div className="mt-10">
              <div className="flex items-baseline gap-3 flex-wrap">
                <div data-testid="pdp-price" className={variant.contact_only ? "font-display text-[color:var(--bl-gold)] text-2xl md:text-3xl" : "font-display text-white text-5xl"}>{variant.contact_only ? "Contact for availability" : `A$${variant.price.toFixed(2)}`}</div>
                {variant.original_price && variant.original_price !== variant.price && (
                  <>
                    <div className="font-mono text-lg text-white/40 line-through">A${variant.original_price.toFixed(2)}</div>
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bl-blue)] border border-[color:var(--bl-blue)]/40 px-2 py-1">
                      SAVE 25%
                    </span>
                  </>
                )}
                {typeof variant.stock === "number" && variant.stock > 0 && variant.stock <= 5 && (
                  <span data-testid="pdp-low-stock" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bl-gold)] border border-[color:var(--bl-gold)]/40 px-2 py-1 animate-pulse">
                    Only {variant.stock} left
                  </span>
                )}
                {typeof variant.stock === "number" && variant.stock <= 0 && (
                  <span data-testid="pdp-sold-out" className="font-mono text-[10px] tracking-[0.22em] uppercase text-red-300 border border-red-400/40 px-2 py-1">
                    Sold out
                  </span>
                )}
              </div>
              <div className="mt-2 font-mono text-[12px] tracking-[0.15em] text-white/50">{variant.unit_label || product.unit_label || "10 vials per pack"}</div>
              {variant.batch_size && variant.batch_size > 0 && (
                <div className="mt-4 p-4 border border-[color:var(--bl-gold)]/30 bg-[color:var(--bl-gold)]/[0.04]" data-testid="pdp-batch-badge">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="bl-eyebrow text-[color:var(--bl-gold)]">Limited Batch</div>
                    <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/70">
                      <span className="text-white text-base font-display">{Math.max(0, variant.stock ?? 0)}</span>
                      <span className="text-white/40"> / {variant.batch_size}</span>
                      <span className="ml-1 text-white/50"> {variant.vials === 1 ? "vials" : "packs"} left</span>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 bg-white/8 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[color:var(--bl-gold)] to-[color:var(--bl-blue)] transition-all duration-500"
                      style={{ width: `${Math.max(0, Math.min(100, ((variant.stock ?? 0) / variant.batch_size) * 100))}%` }}
                    />
                  </div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {variant.batch_size - (variant.stock ?? 0)} sold{product.kind === "peptide" && variant.vials !== 1 ? " · 10 vials per pack · one-time production run" : ""}
                  </div>
                </div>
              )}
            </div>

            {/* Qty + Add to cart */}
            {variant.contact_only ? (
              <div className="mt-8" data-testid="pdp-contact-only">
                <div className="bl-eyebrow mb-3">Availability</div>
                <p className="text-white/60 text-sm mb-4">This strength is supplied on enquiry. Tell us the quantity you need and we'll confirm the current batch and pricing.</p>
                <a href={`mailto:sales@regenbiolabz.com?subject=${encodeURIComponent(`Availability enquiry: ${product.name} ${variant.strength}`)}`} data-testid="pdp-contact-btn"
                   className="flex h-14 items-center justify-center bg-[color:var(--bl-gold)] hover:bg-[color:var(--bl-gold)]/85 text-black font-mono text-[12px] tracking-[0.25em] uppercase">
                  Contact sales for availability
                </a>
              </div>
            ) : (
            <div className="mt-8">
              <div className="bl-eyebrow mb-3">Quantity</div>
              <div className="flex items-stretch gap-3">
                <div className="inline-flex items-center border border-white/15 h-14 w-32 justify-between shrink-0">
                  <button data-testid="pdp-qty-dec" onClick={() => setQty(q => Math.max(1, q - 1))} className="w-10 h-full grid place-items-center text-white/70 hover:text-white"><Minus size={14} /></button>
                  <div className="font-mono text-base">{qty}</div>
                  <button data-testid="pdp-qty-inc" onClick={() => setQty(q => q + 1)} className="w-10 h-full grid place-items-center text-white/70 hover:text-white"><Plus size={14} /></button>
                </div>
                <button
                  data-testid="pdp-add-to-cart"
                  onClick={handleAdd}
                  disabled={typeof variant.stock === "number" && variant.stock <= 0}
                  className="flex-1 h-14 bg-[color:var(--bl-blue)] hover:bg-[color:var(--bl-blue)]/85 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed text-black font-mono text-[13px] tracking-[0.25em] uppercase transition-colors"
                >
                  {typeof variant.stock === "number" && variant.stock <= 0
                    ? "Sold Out"
                    : `Add to Cart · A$${(variant.price * qty).toFixed(2)}`}
                </button>
              </div>
            </div>
            )}

            <DeliveryPromise className="mt-3" />

            {/* Shipping + total */}
            <div className="mt-5 flex items-center justify-between gap-3 text-[13px]">
              <div className="inline-flex items-center gap-2 text-[color:var(--bl-blue)]">
                <Truck size={16} /> <span>Ships from Australia</span>
              </div>
              <TrustSeal compact />
              <div className="font-mono text-white/50 text-[12px] tracking-[0.15em]">{(variant.vials ?? product.vials_per_unit) ? `${qty * (variant.vials ?? product.vials_per_unit)} vials total` : `${qty} × ${variant.unit_label || product.unit_label || "unit"}`}</div>
            </div>

            {/* Accordion sections */}
            <div className="mt-10 border-t border-white/8">
              <Accordion type="single" collapsible>
                <AccordionItem value="overview" className="border-white/8">
                  <AccordionTrigger data-testid="pdp-acc-overview" className="text-white hover:no-underline hover:text-[color:var(--bl-blue)] py-5">
                    <span className="flex items-center gap-3 font-mono text-[12px] tracking-[0.25em] uppercase">
                      <FileText size={16} className="text-[color:var(--bl-blue)]" /> Product Overview
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed pb-6">
                    {product.description}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="research" className="border-white/8">
                  <AccordionTrigger data-testid="pdp-acc-research" className="text-white hover:no-underline hover:text-[color:var(--bl-blue)] py-5">
                    <span className="flex items-center gap-3 font-mono text-[12px] tracking-[0.25em] uppercase">
                      <FlaskConical size={16} className="text-[color:var(--bl-blue)]" /> Research Information
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed pb-6 space-y-4">
                    <div><span className="bl-eyebrow block mb-1 text-white/45">Mechanism</span>{product.mechanism}</div>
                    <div><span className="bl-eyebrow block mb-1 text-white/45">Notes</span>{product.research_notes}</div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="testing" className="border-white/8">
                  <AccordionTrigger data-testid="pdp-acc-testing" className="text-white hover:no-underline hover:text-[color:var(--bl-blue)] py-5">
                    <span className="flex items-center gap-3 font-mono text-[12px] tracking-[0.25em] uppercase">
                      <ShieldCheck size={16} className="text-[color:var(--bl-blue)]" /> Testing &amp; COA
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed pb-6">
                    {product.coas && product.coas.length > 0 ? (
                      <div className="space-y-4">
                        <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-[color:var(--bl-green)]">
                          ● {product.coas.length} Verified Batch{product.coas.length !== 1 ? "es" : ""}
                        </div>
                        {product.coas.map((c, i) => (
                          <div key={c.batch + i} data-testid={`pdp-coa-${i}`} className="border border-white/8 p-5 bg-white/2">
                            <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                              <div>
                                <div className="bl-eyebrow text-white/40">Batch</div>
                                <div className="font-mono text-sm text-white mt-1 break-all">{c.batch}</div>
                              </div>
                              <div>
                                <div className="bl-eyebrow text-white/40">Analysis</div>
                                <div className="font-mono text-sm text-white mt-1">{c.analysis_date}</div>
                              </div>
                              <div>
                                <div className="bl-eyebrow text-white/40">Purity</div>
                                <div className="font-display text-[color:var(--bl-blue)] text-xl mt-1">{c.purity}</div>
                              </div>
                              <div>
                                <div className="bl-eyebrow text-white/40">Content</div>
                                <div className="text-white/80 text-sm mt-1">{c.content}</div>
                              </div>
                              <div>
                                <div className="bl-eyebrow text-white/40">Lab</div>
                                <div className="text-white/80 text-sm mt-1">{c.lab}</div>
                              </div>
                              <div>
                                <div className="bl-eyebrow text-white/40">Method</div>
                                <div className="text-white/80 text-sm mt-1">{c.methodology || "—"}</div>
                              </div>
                              {c.certificate && (
                                <div className="col-span-2">
                                  <div className="bl-eyebrow text-white/40">Certificate</div>
                                  <div className="font-mono text-xs text-white/80 mt-1">{c.certificate}</div>
                                </div>
                              )}
                            </div>
                            {c.notes && (
                              <div className="mt-4 pt-4 border-t border-white/8 text-white/50 text-xs leading-relaxed">{c.notes}</div>
                            )}
                          </div>
                        ))}
                        <p className="text-white/40 text-xs">
                          Figures reproduced verbatim from the independent lab reports. We do not publish fabricated results.
                        </p>
                      </div>
                    ) : (
                      <div className="text-white/50">
                        Certificate of Analysis pending. Batch data appears here once independent testing clears review.
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="shipping" className="border-white/8">
                  <AccordionTrigger data-testid="pdp-acc-shipping" className="text-white hover:no-underline hover:text-[color:var(--bl-blue)] py-5">
                    <span className="flex items-center gap-3 font-mono text-[12px] tracking-[0.25em] uppercase">
                      <Package size={16} className="text-[color:var(--bl-blue)]" /> Shipping &amp; Storage
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-white/65 leading-relaxed pb-6 space-y-3">
                    <p>Dispatched from Australia. Delivery within Australia takes 4–7 days; international delivery where serviced, timing varies by destination.</p>
                    <p>Store sealed vials refrigerated. Once reconstituted, follow standard laboratory handling. Import compliance is the buyer's responsibility.</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
